import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import api, { fmtIDR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { ShoppingBag, Package, DollarSign, TrendingUp, CheckCircle, Clock, Users as UsersIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import StockManagePanel from "../components/StockManagePanel";
import KycReviewPanel from "../components/KycReviewPanel";
import SupplierMonitorPanel from "../components/SupplierMonitorPanel";
import { hasPerm } from "../lib/perms";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-[#202127] border border-[#35353B] rounded-lg p-5 hover:border-[#D4AF37] transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A8A8AD]">{label}</div>
        <Icon className="text-[#D4AF37]" size={18} />
      </div>
      <div className="text-2xl font-bold text-[#F5F3ED]" style={{fontFamily:"'Playfair Display', serif"}}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [stockSubs, setStockSubs] = useState([]);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    const path = `/dashboard/${user.role}`;
    api.get(path).then(r => setData(r.data));
    if (user.role === "owner") {
      api.get("/reports/summary").then(r => setReports(r.data));
    } else if (user.role === "staff") {
      api.get("/orders").then(r => setStockSubs(r.data.filter(o => ["waiting_payment","verifying","preparing","shipped"].includes(o.status)).slice(0, 10)));
    }
  }, [user.role]);

  if (!data) return <div className="min-h-screen bg-[#101114]"><Header /><div className="p-10 text-[#A8A8AD]">{t("common.loading")}</div></div>;

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">{user.role.toUpperCase()} DASHBOARD</div>
            <h1 className="text-3xl font-bold text-[#F5F3ED]" style={{fontFamily:"'Playfair Display', serif"}}>Selamat datang, {user.name}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {user.role !== "supplier" && <Link to="/orders" data-testid="dash-orders-link" className="border border-[#35353B] text-[#F5F3ED] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm px-4 py-2 text-sm">Pesanan</Link>}
            {hasPerm(user, "manage_demand") && <Link to="/demand-board" data-testid="dash-demand-link" className="border border-[#35353B] text-[#F5F3ED] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm px-4 py-2 text-sm">Demand Board</Link>}
            {hasPerm(user, "manage_users") && <Link to="/members" data-testid="dash-members-link" className="border border-[#35353B] text-[#F5F3ED] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm px-4 py-2 text-sm">Kelola Pengguna</Link>}
            {user.role === "owner" && <Link to="/admin" data-testid="dash-admin-link" className="bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] font-bold rounded-sm px-4 py-2 text-sm">Panel Kelola</Link>}
            {user.role === "supplier" && <Link to="/jobs" data-testid="dash-jobs-link" className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] font-bold rounded-sm px-4 py-2 text-sm">Available Jobs</Link>}
            {user.role === "supplier" && <Link to="/supplier/submit" data-testid="dash-submit-stock-link" className="bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] font-bold rounded-sm px-4 py-2 text-sm">{t("nav.submit_stock")}</Link>}
          </div>
        </div>

        {/* Role-specific stats */}
        {user.role === "owner" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <StatCard testId="stat-new-orders" icon={ShoppingBag} label={t("dashboard.new_orders")} value={data.new_orders} />
              <StatCard testId="stat-to-process" icon={Clock} label={t("dashboard.to_process")} value={data.to_process} />
              <StatCard testId="stat-completed" icon={CheckCircle} label={t("dashboard.completed")} value={data.completed} />
              <StatCard testId="stat-total-sales" icon={DollarSign} label={t("dashboard.total_sales")} value={fmtIDR(data.total_sales)} />
              <StatCard testId="stat-stock" icon={Package} label={t("dashboard.stock_available")} value={data.stock_available} />
              <StatCard testId="stat-reviews" icon={FileText} label="Catatan Stok Supplier Hari Ini" value={data.supplier_records_today} />
            </div>

            {reports && (
              <div className="bg-[#202127] border border-[#35353B] rounded-lg p-6 mb-8">
                <h2 className="text-xl font-bold text-[#F5F3ED] mb-4" style={{fontFamily:"'Playfair Display', serif"}}>Laporan Ringkas</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><div className="text-xs text-[#A8A8AD] uppercase tracking-widest">Revenue Terkonfirmasi</div><div className="text-lg text-[#35C78A] font-bold">{fmtIDR(reports.confirmed_revenue)}</div></div>
                  <div><div className="text-xs text-[#A8A8AD] uppercase tracking-widest">Biaya Supplier (Alokasi Selesai)</div><div className="text-lg text-[#F06464] font-bold">{fmtIDR(reports.supplier_cost)}</div></div>
                  <div><div className="text-xs text-[#A8A8AD] uppercase tracking-widest">Margin Kotor</div><div className="text-lg text-[#D4AF37] font-bold">{fmtIDR(reports.gross_margin)}</div></div>
                  <div><div className="text-xs text-[#A8A8AD] uppercase tracking-widest">Pembayaran Pending</div><div className="text-lg text-[#A8A8AD] font-bold">{fmtIDR(reports.pending_payments)}</div></div>
                </div>
              </div>
            )}

            <KycReviewPanel />
            <StockManagePanel />
          </>
        )}

        {user.role === "staff" && (
          <>
            {hasPerm(user, "review_kyc") && <KycReviewPanel />}
            {hasPerm(user, "monitor_suppliers") && <SupplierMonitorPanel />}
            {hasPerm(user, "manage_stock") && <StockManagePanel />}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <StatCard testId="stat-new" icon={ShoppingBag} label="Pesanan Baru" value={data.new_orders} />
              <StatCard testId="stat-verify" icon={Clock} label="Verifikasi" value={data.verify_payment} />
              <StatCard testId="stat-preparing" icon={Package} label="Menyiapkan" value={data.preparing} />
              <StatCard testId="stat-ship" icon={TrendingUp} label="Perlu Dikirim" value={data.to_ship} />
              <StatCard testId="stat-done" icon={CheckCircle} label="Selesai" value={data.completed} />
            </div>
            <div className="bg-[#202127] border border-[#35353B] rounded-lg p-6">
              <h2 className="text-xl font-bold text-[#F5F3ED] mb-4" style={{fontFamily:"'Playfair Display', serif"}}>Pekerjaan Perlu Diselesaikan</h2>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]">
                  <th className="py-2">No.</th><th className="py-2">Produk</th><th className="py-2 text-right">Total</th><th className="py-2">Status</th><th className="py-2"></th>
                </tr></thead>
                <tbody>
                  {stockSubs.map(o => (
                    <tr key={o.id} className="border-b border-[#35353B]">
                      <td className="py-3 font-mono text-[#D4AF37]">{o.order_no}</td>
                      <td className="py-3 text-[#F5F3ED]">{o.items.map(i=>i.product_name).join(", ")}</td>
                      <td className="py-3 text-right text-[#D4AF37]">{fmtIDR(o.total_idr)}</td>
                      <td className="py-3 text-xs text-[#A8A8AD]">{t(`order.status.${o.status}`)}</td>
                      <td className="py-3"><Link to={`/orders/${o.id}`} className="text-[#D4AF37] hover:underline text-xs">Buka →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {user.role === "supplier" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard testId="stat-sup-records" icon={FileText} label="Catatan Stok" value={data.records} />
          </div>
        )}
      </div>
    </div>
  );
}
